import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CaseflowContext } from '../../../context/CaseflowContext';
import { UserContext } from '../../../context/UserContext';
import { AppContext } from '@rabbit/app-context';
import { generateGeminiResponse } from '@rabbit/utils/gemini-ai';

// Define the types for the chatbot messages
export type MessageType = 'user' | 'bot' | 'system';

export interface ChatMessage {
  id: string;
  text: string;
  type: MessageType;
  timestamp: Date;
  isLoading?: boolean;
  actions?: ChatAction[];
}

export interface ChatAction {
  label: string;
  action: () => void;
}

// Define the types for the chatbot intents
export enum ChatbotIntent {
  GREETING = 'greeting',
  CREATE_REGISTRATION = 'create_registration',
  CREATE_CLAIM = 'create_claim',
  FETCH_REGISTRATION_DETAILS = 'fetch_registration_details',
  FETCH_CLAIM_DETAILS = 'fetch_claim_details',
  UPDATE_WARRANTY = 'update_warranty',
  UPDATE_CLAIM = 'update_claim',
  WARRANTY_STATUS = 'warranty_status',
  HELP = 'help',
  UNKNOWN = 'unknown',
}

// Define the context interface
interface ChatbotContextProps {
  messages: ChatMessage[];
  isOpen: boolean;
  isMinimized: boolean;
  useGemini: boolean;
  addMessage: (text: string, type: MessageType, actions?: ChatAction[]) => void;
  sendMessage: (text: string) => void;
  toggleChatbot: () => void;
  minimizeChatbot: () => void;
  maximizeChatbot: () => void;
  clearMessages: () => void;
  toggleGemini: () => void;
}

// Create the context
export const ChatbotContext = createContext<ChatbotContextProps | undefined>(
  undefined
);

// Create the provider component
interface ChatbotProviderProps {
  children: ReactNode;
}

export const ChatbotProvider: React.FC<ChatbotProviderProps> = ({
  children,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [conversationHistory, setConversationHistory] = useState<string[]>([]);
  const [useGemini, setUseGemini] = useState<boolean>(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const caseflowContext = useContext(CaseflowContext);
  const userContext = useContext(UserContext);
  const appContext = useContext(AppContext);

  // Initialize the chatbot with a welcome message
  useEffect(() => {
    if (messages.length === 0) {
      addMessage(
        t(
          'chatbot.welcomeMessage',
          'Hello! I am your AI assistant. How can I help you today?'
        ),
        'bot',
        [
          {
            label: t('chatbot.warrantyStatus', 'Warranty Status for Roofing Shingles, Insulation, Fiberglass'),
            action: () => handleIntent(ChatbotIntent.WARRANTY_STATUS),
          },
          {
            label: t('chatbot.help', 'Help'),
            action: () => handleIntent(ChatbotIntent.HELP),
          },
        ]
      );
    }
  }, []);

  // Add a message to the chat
  const addMessage = (
    text: string,
    type: MessageType,
    actions: ChatAction[] = []
  ) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      text,
      type,
      timestamp: new Date(),
      actions,
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);

    // Update conversation history for Gemini AI
    if (type === 'user' || type === 'bot') {
      setConversationHistory((prev) => [...prev, text]);
    }
  };

  // Determine the intent of a message
  const determineIntent = (text: string): ChatbotIntent => {
    const lowerText = text.toLowerCase();

    if (
      lowerText.includes('hello') ||
      lowerText.includes('hi') ||
      lowerText.includes('hey')
    ) {
      return ChatbotIntent.GREETING;
    } else if (
      lowerText.includes('warranty status') ||
      lowerText.includes('check warranty') ||
      lowerText.includes('warranty information') ||
      lowerText.includes('roofing shingles') ||
      lowerText.includes('insulation warranty') ||
      lowerText.includes('fiberglass warranty') ||
      (lowerText.includes('warranty') &&
        (lowerText.includes('roofing') ||
         lowerText.includes('shingles') ||
         lowerText.includes('insulation') ||
         lowerText.includes('fiberglass')))
    ) {
      return ChatbotIntent.WARRANTY_STATUS;
    } else if (
      lowerText.includes('create registration') ||
      lowerText.includes('new registration') ||
      lowerText.includes('register')
    ) {
      return ChatbotIntent.CREATE_REGISTRATION;
    } else if (
      lowerText.includes('create claim') ||
      lowerText.includes('new claim') ||
      lowerText.includes('make claim')
    ) {
      return ChatbotIntent.CREATE_CLAIM;
    } else if (
      lowerText.includes('registration details') ||
      lowerText.includes('get registration') ||
      lowerText.includes('show registration')
    ) {
      return ChatbotIntent.FETCH_REGISTRATION_DETAILS;
    } else if (
      lowerText.includes('claim details') ||
      lowerText.includes('get claim') ||
      lowerText.includes('show claim')
    ) {
      return ChatbotIntent.FETCH_CLAIM_DETAILS;
    } else if (
      lowerText.includes('update warranty') ||
      lowerText.includes('edit warranty') ||
      lowerText.includes('change warranty')
    ) {
      return ChatbotIntent.UPDATE_WARRANTY;
    } else if (
      lowerText.includes('update claim') ||
      lowerText.includes('edit claim') ||
      lowerText.includes('change claim')
    ) {
      return ChatbotIntent.UPDATE_CLAIM;
    } else if (
      lowerText.includes('help') ||
      lowerText.includes('assist') ||
      lowerText.includes('support')
    ) {
      return ChatbotIntent.HELP;
    } else {
      return ChatbotIntent.UNKNOWN;
    }
  };

  // Handle different intents
  const handleIntent = (intent: ChatbotIntent, text?: string) => {
    switch (intent) {
      case ChatbotIntent.GREETING:
        addMessage(
          t('chatbot.greeting', 'Hello! How can I assist you today?'),
          'bot',
          [
            {
              label: t('chatbot.warrantyStatus', 'Warranty Status for Roofing Shingles, Insulation, Fiberglass'),
              action: () => handleIntent(ChatbotIntent.WARRANTY_STATUS),
            },
          ]
        );
        break;
      case ChatbotIntent.CREATE_REGISTRATION:
        addMessage(
          t(
            'chatbot.createRegistrationPrompt',
            'I can help you create a new registration. Would you like to proceed?'
          ),
          'bot',
          [
            {
              label: t('general.yes', 'Yes'),
              action: () => {
                navigate('/registrations/new');
                addMessage(
                  t(
                    'chatbot.navigatingToRegistration',
                    'Navigating to the registration page...'
                  ),
                  'system'
                );
              },
            },
            {
              label: t('general.no', 'No'),
              action: () => {
                addMessage(
                  t(
                    'chatbot.cancelledRegistration',
                    'Registration creation cancelled. How else can I help you?'
                  ),
                  'bot'
                );
              },
            },
          ]
        );
        break;
      case ChatbotIntent.CREATE_CLAIM:
        addMessage(
          t(
            'chatbot.createClaimPrompt',
            'I can help you create a new claim. Would you like to proceed?'
          ),
          'bot',
          [
            {
              label: t('general.yes', 'Yes'),
              action: () => {
                navigate('/claims/new');
                addMessage(
                  t(
                    'chatbot.navigatingToClaim',
                    'Navigating to the claim creation page...'
                  ),
                  'system'
                );
              },
            },
            {
              label: t('general.no', 'No'),
              action: () => {
                addMessage(
                  t(
                    'chatbot.cancelledClaim',
                    'Claim creation cancelled. How else can I help you?'
                  ),
                  'bot'
                );
              },
            },
          ]
        );
        break;
      case ChatbotIntent.FETCH_REGISTRATION_DETAILS:
        addMessage(
          t(
            'chatbot.fetchRegistrationPrompt',
            'I can help you find registration details. Please provide the registration ID or product name.'
          ),
          'bot'
        );
        break;
      case ChatbotIntent.FETCH_CLAIM_DETAILS:
        addMessage(
          t(
            'chatbot.fetchClaimPrompt',
            'I can help you find claim details. Please provide the claim ID.'
          ),
          'bot'
        );
        break;
      case ChatbotIntent.UPDATE_WARRANTY:
        addMessage(
          t(
            'chatbot.updateWarrantyPrompt',
            'I can help you update warranty details. Please provide the warranty ID.'
          ),
          'bot'
        );
        break;
      case ChatbotIntent.UPDATE_CLAIM:
        addMessage(
          t(
            'chatbot.updateClaimPrompt',
            'I can help you update claim details. Please provide the claim ID.'
          ),
          'bot'
        );
        break;
      case ChatbotIntent.HELP:
        addMessage(
          t(
            'chatbot.helpMessage',
            'I can help you with the following tasks:\n- Check warranty status for roofing shingles, insulation, fiberglass\n- Find registration details\n- Find claim details\n- Update warranty details\n- Update claim details\n\nWhat would you like to do?'
          ),
          'bot',
          [
            {
              label: t('chatbot.warrantyStatus', 'Warranty Status for Roofing Shingles, Insulation, Fiberglass'),
              action: () => handleIntent(ChatbotIntent.WARRANTY_STATUS),
            },
            {
              label: t('chatbot.fetchRegistration', 'Find Registration'),
              action: () =>
                handleIntent(ChatbotIntent.FETCH_REGISTRATION_DETAILS),
            },
            {
              label: t('chatbot.fetchClaim', 'Find Claim'),
              action: () => handleIntent(ChatbotIntent.FETCH_CLAIM_DETAILS),
            },
          ]
        );
        break;
      case ChatbotIntent.WARRANTY_STATUS:
        addMessage(
          t(
            'chatbot.warrantyStatusPrompt',
            'I can help you check the warranty status for Owens Corning products including roofing shingles, insulation, and fiberglass. Please select a product category:'
          ),
          'bot',
          [
            {
              label: t('chatbot.roofingShingles', 'Roofing Shingles'),
              action: () => {
                addMessage(
                  t(
                    'chatbot.roofingShinglesInfo',
                    'Owens Corning offers limited lifetime warranties for most residential roofing shingles. The exact coverage depends on the product line and installation date. To check your specific warranty details, please provide your product name or registration number.'
                  ),
                  'bot'
                );
              },
            },
            {
              label: t('chatbot.insulation', 'Insulation'),
              action: () => {
                addMessage(
                  t(
                    'chatbot.insulationInfo',
                    'Owens Corning insulation products typically come with a limited lifetime warranty when installed in a residential application. For commercial applications, warranty periods may vary. To check your specific warranty details, please provide your product name or registration number.'
                  ),
                  'bot'
                );
              },
            },
            {
              label: t('chatbot.fiberglass', 'Fiberglass'),
              action: () => {
                addMessage(
                  t(
                    'chatbot.fiberglassInfo',
                    'Owens Corning fiberglass products come with different warranty periods depending on the specific product and application. To check your specific warranty details, please provide your product name or registration number.'
                  ),
                  'bot'
                );
              },
            },
          ]
        );
        break;
      case ChatbotIntent.UNKNOWN:
        addMessage(
          t(
            'chatbot.unknownIntent',
            "I'm sorry, I didn't understand that. How can I help you?"
          ),
          'bot',
          [
            {
              label: t('chatbot.help', 'Help'),
              action: () => handleIntent(ChatbotIntent.HELP),
            },
          ]
        );
        break;
    }
  };

  // Send a message from the user
  const sendMessage = async (text: string) => {
    if (!text.trim()) return;

    // Add the user message
    addMessage(text, 'user');

    // Add a loading message from the bot
    const loadingMessageId = Date.now().toString();
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        id: loadingMessageId,
        text: t('chatbot.thinking', 'Thinking...'),
        type: 'bot',
        timestamp: new Date(),
        isLoading: true,
      },
    ]);

    // Determine the intent
    const intent = determineIntent(text);

    // Process the message based on intent
    try {
      // If the intent is unknown and Gemini is enabled, use Gemini AI
      if (intent === ChatbotIntent.UNKNOWN && useGemini) {
        try {
          // Get response from Gemini AI
          const aiResponse = await generateGeminiResponse(
            text,
            conversationHistory
          );

          // Remove the loading message
          setMessages((prevMessages) =>
            prevMessages.filter((message) => message.id !== loadingMessageId)
          );

          // Add the AI response
          addMessage(aiResponse, 'bot', [
            {
              label: t('chatbot.help', 'Help'),
              action: () => handleIntent(ChatbotIntent.HELP),
            },
          ]);
        } catch (error) {
          console.error('Error getting Gemini response:', error);

          // Remove the loading message
          setMessages((prevMessages) =>
            prevMessages.filter((message) => message.id !== loadingMessageId)
          );

          // Add an error message
          addMessage(
            t(
              'chatbot.error',
              'I encountered an error processing your request. Please try again.'
            ),
            'bot',
            [
              {
                label: t('chatbot.help', 'Help'),
                action: () => handleIntent(ChatbotIntent.HELP),
              },
            ]
          );
        }
      } else {
        // For known intents, use the rule-based system
        // Remove the loading message and handle the intent
        setTimeout(() => {
          setMessages((prevMessages) =>
            prevMessages.filter((message) => message.id !== loadingMessageId)
          );
          handleIntent(intent, text);
        }, 1000);
      }
    } catch (error) {
      console.error('Error processing message:', error);

      // Remove the loading message
      setMessages((prevMessages) =>
        prevMessages.filter((message) => message.id !== loadingMessageId)
      );

      // Add an error message
      addMessage(
        t(
          'chatbot.error',
          'I encountered an error processing your request. Please try again.'
        ),
        'bot',
        [
          {
            label: t('chatbot.help', 'Help'),
            action: () => handleIntent(ChatbotIntent.HELP),
          },
        ]
      );
    }
  };

  // Toggle the chatbot open/closed
  const toggleChatbot = () => {
    setIsOpen((prev) => !prev);
    if (isMinimized) {
      setIsMinimized(false);
    }
  };

  // Minimize the chatbot
  const minimizeChatbot = () => {
    setIsMinimized(true);
  };

  // Maximize the chatbot
  const maximizeChatbot = () => {
    setIsMinimized(false);
  };

  // Clear all messages
  const clearMessages = () => {
    setMessages([]);
    // Clear conversation history
    setConversationHistory([]);
    // Add the welcome message back
    addMessage(
      t(
        'chatbot.welcomeMessage',
        'Hello! I am your AI assistant. How can I help you today?'
      ),
      'bot',
      [
        {
          label: t('chatbot.warrantyStatus', 'Warranty Status for Roofing Shingles, Insulation, Fiberglass'),
          action: () => handleIntent(ChatbotIntent.WARRANTY_STATUS),
        },
        {
          label: t('chatbot.help', 'Help'),
          action: () => handleIntent(ChatbotIntent.HELP),
        },
      ]
    );
  };

  // Toggle Gemini AI on/off
  const toggleGemini = () => {
    setUseGemini((prev) => !prev);
    // Add a system message to indicate the change
    addMessage(
      useGemini
        ? t(
            'chatbot.geminiDisabled',
            'Gemini AI has been disabled. Using rule-based responses.'
          )
        : t(
            'chatbot.geminiEnabled',
            'Gemini AI has been enabled. Using AI-powered responses.'
          ),
      'system'
    );
  };

  return (
    <ChatbotContext.Provider
      value={{
        messages,
        isOpen,
        isMinimized,
        useGemini,
        addMessage,
        sendMessage,
        toggleChatbot,
        minimizeChatbot,
        maximizeChatbot,
        clearMessages,
        toggleGemini,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};

// Custom hook to use the chatbot context
export const useChatbot = () => {
  const context = useContext(ChatbotContext);
  if (context === undefined) {
    throw new Error('useChatbot must be used within a ChatbotProvider');
  }
  return context;
};
